/**
 * PC 错误上报用设备环境快照（桌面 / 平板 / 快捷方式 / PWA / 学习机 WebView）
 * 供 error-report 根级 platform 与单条 system 使用。
 */

import { getDeviceType, getViewportWidth, getViewportHeight, isAppleTouchDevice } from '@/utils/viewport.js'
import { isPwaStandalone } from '@/utils/pwaInstall.js'
import {
  getChromiumMajorVersion,
  isLearningDeviceUa,
  isAndroidWebViewUa,
  isLegacyWebView,
} from '@/utils/legacyWebView.js'
import { getViewportMinSide, getViewportMaxSide, isUaTabletHint } from '@/utils/deviceDetect.js'
import {
  isHarmonyOsUa,
  isHarmonyWeChatUa,
  isHarmonyTabletUa,
  isHarmonyMobileUa,
} from '@/utils/uaHarmony.js'

function safeMatch (ua, re) {
  try {
    const m = String(ua).match(re)
    return m || null
  } catch (e) {
    return null
  }
}

function matchMediaSafe (query) {
  try {
    if (typeof window === 'undefined' || !window.matchMedia) return null
    return window.matchMedia(query).matches
  } catch (e) {
    return null
  }
}

function getDisplayMode () {
  if (typeof window === 'undefined') return 'unknown'
  const modes = ['fullscreen', 'standalone', 'minimal-ui', 'browser']
  for (const mode of modes) {
    if (matchMediaSafe(`(display-mode: ${mode})`)) return mode
  }
  // iOS Safari「添加到主屏幕」
  try {
    if (window.navigator?.standalone === true) return 'standalone'
  } catch (e) { /* ignore */ }
  return 'browser'
}

function parseBrowser (ua) {
  const s = String(ua || '')
  let name = 'Unknown'
  let version = ''
  let engine = ''
  let engineVersion = ''

  // 顺序：先特殊再通用
  if (/MicroMessenger/i.test(s)) {
    name = 'WeChat'
    version = safeMatch(s, /MicroMessenger\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/Edg(?:e|A|iOS)?\//i.test(s)) {
    name = 'Edge'
    version = safeMatch(s, /Edg(?:e|A|iOS)?\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/OPR\/|Opera\//i.test(s)) {
    name = 'Opera'
    version = safeMatch(s, /(?:OPR|Opera)\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/SamsungBrowser\//i.test(s)) {
    name = 'SamsungBrowser'
    version = safeMatch(s, /SamsungBrowser\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/HuaweiBrowser\//i.test(s) || /HBPC\//i.test(s)) {
    name = 'HuaweiBrowser'
    version = safeMatch(s, /(?:HuaweiBrowser|HBPC)\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/QQBrowser\//i.test(s) || /MQQBrowser\//i.test(s)) {
    name = 'QQBrowser'
    version = safeMatch(s, /(?:MQQBrowser|QQBrowser)\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/UCBrowser\//i.test(s)) {
    name = 'UCBrowser'
    version = safeMatch(s, /UCBrowser\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/Firefox\//i.test(s)) {
    name = 'Firefox'
    version = safeMatch(s, /Firefox\/([\d.]+)/i)?.[1] || ''
    engine = 'Gecko'
    engineVersion = safeMatch(s, /rv:([\d.]+)/)?.[1] || ''
  } else if (/Chrome\//i.test(s) && !/Chromium\//i.test(s)) {
    name = 'Chrome'
    version = safeMatch(s, /Chrome\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/Chromium\//i.test(s)) {
    name = 'Chromium'
    version = safeMatch(s, /Chromium\/([\d.]+)/i)?.[1] || ''
    engine = 'Blink'
  } else if (/Safari\//i.test(s) && !/Chrome\//i.test(s)) {
    name = 'Safari'
    version = safeMatch(s, /Version\/([\d.]+)/i)?.[1] || ''
    engine = 'WebKit'
    engineVersion = safeMatch(s, /AppleWebKit\/([\d.]+)/i)?.[1] || ''
  } else if (/MSIE |Trident\//i.test(s)) {
    name = 'IE'
    version = safeMatch(s, /(?:MSIE |rv:)([\d.]+)/)?.[1] || ''
    engine = 'Trident'
  }

  if (!engine) {
    if (/AppleWebKit/i.test(s)) {
      engine = 'WebKit'
      engineVersion = safeMatch(s, /AppleWebKit\/([\d.]+)/i)?.[1] || ''
    } else if (/Gecko/i.test(s)) {
      engine = 'Gecko'
    }
  }
  if (engine === 'Blink' && !engineVersion) {
    // Blink 版本通常与 Chrome 主版本同号
    engineVersion = version
  }

  const major = version ? Number(String(version).split('.')[0]) : null
  const chromiumMajor = getChromiumMajorVersion(s)

  return {
    name,
    version,
    major: Number.isFinite(major) ? major : null,
    engine,
    engineVersion,
    chromiumMajor,
    full: version ? `${name} ${version}` : name,
  }
}

function parseOS (ua) {
  const s = String(ua || '')
  let name = 'Unknown'
  let version = ''

  if (isHarmonyOsUa(s)) {
    name = 'HarmonyOS'
    version = safeMatch(s, /HarmonyOS\s*([\d.]+)/i)?.[1]
      || safeMatch(s, /OpenHarmony[/\s]?([\d.]+)/i)?.[1]
      || ''
  } else if (/Windows NT 10\.0/i.test(s)) {
    name = 'Windows'
    version = '10/11'
  } else if (/Windows NT 6\.3/i.test(s)) {
    name = 'Windows'
    version = '8.1'
  } else if (/Windows NT 6\.2/i.test(s)) {
    name = 'Windows'
    version = '8'
  } else if (/Windows NT 6\.1/i.test(s)) {
    name = 'Windows'
    version = '7'
  } else if (/Windows/i.test(s)) {
    name = 'Windows'
  } else if (/Android/i.test(s)) {
    name = 'Android'
    version = safeMatch(s, /Android\s+([\d.]+)/i)?.[1] || ''
  } else if (/iPhone|iPad|iPod/i.test(s) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1)) {
    // iPadOS 13+ 可能伪装 Macintosh
    const isIpadOs = /iPad/i.test(s) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1 && !/iPhone/i.test(s))
    name = isIpadOs ? 'iPadOS' : 'iOS'
    const m = safeMatch(s, /(?:iPhone )?OS (\d+)[._](\d+)(?:[._](\d+))?/i)
      || safeMatch(s, /Version\/(\d+)[._](\d+)/i)
    if (m) version = m[3] ? `${m[1]}.${m[2]}.${m[3]}` : `${m[1]}.${m[2]}`
  } else if (/Mac OS X/i.test(s)) {
    name = 'macOS'
    const m = safeMatch(s, /Mac OS X (\d+)[._](\d+)(?:[._](\d+))?/i)
    if (m) version = m[3] ? `${m[1]}.${m[2]}.${m[3]}` : `${m[1]}.${m[2]}`
  } else if (/CrOS/i.test(s)) {
    name = 'ChromeOS'
  } else if (/Linux/i.test(s)) {
    name = 'Linux'
  }

  let platform = ''
  let arch = ''
  try {
    platform = navigator.platform || ''
    arch = navigator.userAgentData?.architecture || ''
  } catch (e) { /* ignore */ }

  return {
    name,
    version,
    full: version ? `${name} ${version}` : name,
    platform,
    arch,
  }
}

function parseDeviceModel (ua) {
  const s = String(ua || '')
  if (/iPad/i.test(s) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1 && !/iPhone/i.test(s))) {
    return { brand: 'Apple', model: 'iPad' }
  }
  if (/iPhone/i.test(s)) return { brand: 'Apple', model: 'iPhone' }
  if (/iPod/i.test(s)) return { brand: 'Apple', model: 'iPod' }
  if (/Macintosh/i.test(s)) return { brand: 'Apple', model: 'Mac' }

  if (/Android/i.test(s) || isHarmonyOsUa(s)) {
    const m = safeMatch(s, /\(([^)]+)\)/)
    if (m?.[1]) {
      const parts = m[1].split(';').map((p) => p.trim()).filter(Boolean)
      for (const part of parts) {
        if (
          part &&
          !/Android|Build|Linux|Mobile|wv|U|HarmonyOS|OpenHarmony|Phone|Tablet|PC/i.test(part) &&
          part.length > 1 &&
          part.length < 40
        ) {
          let brand = ''
          if (/HUAWEI|Huawei/i.test(s)) brand = 'HUAWEI'
          else if (/Honor|HONOR/i.test(s)) brand = 'Honor'
          else if (/Xiaomi|Redmi|MI /i.test(s)) brand = 'Xiaomi'
          else if (/OPPO/i.test(s)) brand = 'OPPO'
          else if (/vivo/i.test(s)) brand = 'vivo'
          else if (/Samsung|SM-/i.test(s)) brand = 'Samsung'
          else if (/Lenovo/i.test(s)) brand = 'Lenovo'
          return { brand, model: part }
        }
      }
    }
  }

  if (/Windows/i.test(s)) return { brand: '', model: 'PC' }
  return { brand: '', model: '' }
}

function getClientHintsSync () {
  try {
    const uaData = navigator.userAgentData
    if (!uaData) return null
    return {
      mobile: typeof uaData.mobile === 'boolean' ? uaData.mobile : null,
      platform: uaData.platform || '',
      brands: Array.isArray(uaData.brands)
        ? uaData.brands.map((b) => ({ brand: b.brand, version: b.version }))
        : [],
    }
  } catch (e) {
    return null
  }
}

function getNetworkInfo () {
  try {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    return {
      online: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
      effectiveType: c?.effectiveType || '',
      downlink: typeof c?.downlink === 'number' ? c.downlink : null,
      rtt: typeof c?.rtt === 'number' ? c.rtt : null,
      saveData: typeof c?.saveData === 'boolean' ? c.saveData : null,
    }
  } catch (e) {
    return { online: null }
  }
}

function getScreenInfo () {
  try {
    const s = window.screen || {}
    const orient = s.orientation?.type || ''
    return {
      width: s.width || 0,
      height: s.height || 0,
      availWidth: s.availWidth || 0,
      availHeight: s.availHeight || 0,
      colorDepth: s.colorDepth || 0,
      pixelDepth: s.pixelDepth || 0,
      orientation: orient || (window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait'),
    }
  } catch (e) {
    return {}
  }
}

function getViewportInfo () {
  try {
    const vv = window.visualViewport
    return {
      innerWidth: window.innerWidth || 0,
      innerHeight: window.innerHeight || 0,
      clientWidth: document.documentElement?.clientWidth || 0,
      clientHeight: document.documentElement?.clientHeight || 0,
      visualWidth: vv?.width || null,
      visualHeight: vv?.height || null,
      minSide: getViewportMinSide(),
      maxSide: getViewportMaxSide(),
      cssWidth: getViewportWidth(),
      cssHeight: getViewportHeight(),
    }
  } catch (e) {
    return {}
  }
}

function getTouchInfo () {
  const hasTouch = (() => {
    try {
      return 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0
    } catch (e) {
      return false
    }
  })()
  return {
    hasTouch,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    pointerFine: matchMediaSafe('(pointer: fine)'),
    pointerCoarse: matchMediaSafe('(pointer: coarse)'),
    anyPointerFine: matchMediaSafe('(any-pointer: fine)'),
    anyPointerCoarse: matchMediaSafe('(any-pointer: coarse)'),
    hover: matchMediaSafe('(hover: hover)'),
    anyHover: matchMediaSafe('(any-hover: hover)'),
  }
}

function getLaunchInfo () {
  const displayMode = getDisplayMode()
  const standalone = isPwaStandalone()
  let fullscreenApi = false
  try {
    fullscreenApi = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    )
  } catch (e) { /* ignore */ }
  return {
    displayMode,
    isPwaStandalone: standalone,
    /** 桌面/平板「添加到主屏幕 / 创建快捷方式」独立窗口 */
    isShortcut: standalone || displayMode === 'standalone' || displayMode === 'minimal-ui',
    isFullscreenApi: fullscreenApi,
  }
}

/**
 * 采集完整设备环境（同步；适合错误上报入队时调用）
 * @returns {object}
 */
export function getDeviceReportInfo () {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { platform: 'web', environment: 'PC' }
  }

  try {
    const ua = navigator.userAgent || ''
    const browser = parseBrowser(ua)
    const os = parseOS(ua)
    const { brand, model } = parseDeviceModel(ua)
    const deviceType = getDeviceType() // desktop | tablet | mobile
    const touch = getTouchInfo()
    const launch = getLaunchInfo()
    const screen = getScreenInfo()
    const viewport = getViewportInfo()
    const network = getNetworkInfo()
    const clientHints = getClientHintsSync()
    const pixelRatio = Number(window.devicePixelRatio) || 1

    const flags = {
      isWechat: /MicroMessenger/i.test(ua),
      isLearningDevice: isLearningDeviceUa(ua),
      isAndroidWebView: isAndroidWebViewUa(ua),
      isLegacyWebView: false,
      isHarmony: isHarmonyOsUa(ua),
      isHarmonyWeChat: isHarmonyWeChatUa(ua),
      isHarmonyTablet: isHarmonyTabletUa(ua),
      isHarmonyMobile: isHarmonyMobileUa(ua),
      isAppleTouch: isAppleTouchDevice(),
      isUaTabletHint: isUaTabletHint(ua),
    }
    try {
      flags.isLegacyWebView = isLegacyWebView()
    } catch (e) { /* ignore */ }

    // 兼容移动端同名字段 + PC 扩展
    return {
      // —— 兼容字段（与移动端 getDeviceReportInfo 对齐）——
      platform: 'web',
      system: os.full,
      version: browser.full,
      model,
      brand,
      userAgent: ua,

      // —— PC 扩展：设备归类 ——
      environment: 'PC',
      deviceType,
      deviceCategory: deviceType,

      // —— 系统 / 浏览器 / 内核 ——
      os,
      browser,
      engine: {
        name: browser.engine,
        version: browser.engineVersion,
        chromiumMajor: browser.chromiumMajor,
      },

      // —— 屏幕 / 视口 / 像素比 ——
      screen,
      viewport,
      pixelRatio,
      devicePixelRatio: pixelRatio,

      // —— 触摸 / 指针 ——
      touch,
      hasTouch: touch.hasTouch,
      maxTouchPoints: touch.maxTouchPoints,

      // —— 启动形态（浏览器 Tab / 快捷方式 / PWA）——
      launch,
      displayMode: launch.displayMode,
      isPwaStandalone: launch.isPwaStandalone,
      isShortcut: launch.isShortcut,

      // —— 网络 / 语言 / 硬件 ——
      network,
      locale: {
        language: navigator.language || '',
        languages: Array.isArray(navigator.languages) ? [...navigator.languages] : [],
        timezone: (() => {
          try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
          } catch (e) {
            return ''
          }
        })(),
        timezoneOffsetMin: new Date().getTimezoneOffset(),
      },
      hardware: {
        concurrency: navigator.hardwareConcurrency || null,
        deviceMemory: typeof navigator.deviceMemory === 'number' ? navigator.deviceMemory : null,
      },

      // —— Client Hints（同步可得部分）——
      clientHints,

      // —— 场景标记 ——
      flags,
    }
  } catch (e) {
    return {
      platform: 'web',
      environment: 'PC',
      userAgent: typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '',
      collectError: String(e?.message || e),
    }
  }
}

export default getDeviceReportInfo
